function canTrack() {
  return typeof window !== "undefined" && typeof window.gtag === "function";
}

function sanitizeValue(value) {
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => sanitizeValue(item))
      .filter((item) => item !== undefined)
      .join(",");
  }

  return undefined;
}

function sanitizeParams(params = {}) {
  const result = {};

  for (const [key, value] of Object.entries(params)) {
    const sanitized = sanitizeValue(value);
    if (sanitized !== undefined) {
      result[key] = sanitized;
    }
  }

  return result;
}

export function trackEvent(name, params = {}) {
  if (!canTrack()) return;
  window.gtag("event", name, sanitizeParams(params));
}

export function trackPageView(path, title) {
  if (!canTrack()) return;
  window.gtag("config", "G-CMM62BSLSP", {
    page_path: path,
    page_title: title,
  });
}

function getVenuePayload(venue = {}) {
  return {
    venue_id: venue.id || venue.slug || undefined,
    venue_slug: venue.slug || undefined,
    venue_name: venue.name || undefined,
    venue_type: venue.type || undefined,
    venue_area: venue.area || venue.areaName || venue.city || undefined,
  };
}

export function trackVenueViewed(venue, context = "detail") {
  trackEvent("venue_viewed", {
    ...getVenuePayload(venue),
    context,
  });
}

export function trackVenueSaved(venue, saved, context = "unknown") {
  trackEvent(saved ? "venue_saved" : "venue_unsaved", {
    ...getVenuePayload(venue),
    context,
  });
}

export function trackVenueVisitClicked(venue, context = "unknown", destination = "maps") {
  trackEvent("venue_visit_clicked", {
    ...getVenuePayload(venue),
    context,
    destination,
  });
}

export function trackVenueShared(venue, method = "native_share") {
  trackEvent("venue_shared", {
    ...getVenuePayload(venue),
    method,
  });
}

export function trackVenueFilterChanged(filter, visibleCount) {
  trackEvent("venue_filter_changed", {
    filter,
    visible_count: visibleCount,
  });
}

export function trackVenueFeedLoaded(venueCount) {
  trackEvent("venue_feed_loaded", {
    venue_count: venueCount,
  });
}

export function trackVenueSwiped(direction, venue) {
  trackEvent("venue_swiped", {
    ...getVenuePayload(venue),
    direction,
  });
}

export function trackExplorerAdViewed(ad, context = "venue_explorer") {
  trackEvent("explorer_ad_viewed", {
    ad_id: ad?.id || undefined,
    zone_id: ad?.zoneId || undefined,
    context,
  });
}

export function trackExplorerAdSwiped(direction, ad, context = "venue_explorer") {
  trackEvent("explorer_ad_swiped", {
    ad_id: ad?.id || undefined,
    zone_id: ad?.zoneId || undefined,
    direction,
    context,
  });
}

export function trackVenueReaction(venue, reaction, context = "unknown") {
  trackEvent("venue_reaction_set", {
    ...getVenuePayload(venue),
    reaction,
    context,
  });
}

export function trackVenueContentOpened(venue, contentType, context = "unknown") {
  trackEvent("venue_content_opened", {
    ...getVenuePayload(venue),
    content_type: contentType,
    context,
  });
}

export function trackVenueContentClosed(venue, contentType, context = "unknown") {
  trackEvent("venue_content_closed", {
    ...getVenuePayload(venue),
    content_type: contentType,
    context,
  });
}

export function trackVenueFilterDrawerToggled(isOpen, context = "unknown") {
  trackEvent(isOpen ? "venue_filter_drawer_opened" : "venue_filter_drawer_closed", {
    context,
  });
}

export function trackAuthDialogOpened(source = "unknown") {
  trackEvent("auth_dialog_opened", { source });
}

export function trackAuthAttempt(provider, mode = "signin") {
  trackEvent("auth_attempt_started", {
    provider,
    mode,
  });
}

export function trackAuthSuccess(provider, mode = "signin") {
  trackEvent("auth_success", {
    provider,
    mode,
  });
}

export function trackAuthFailure(provider, mode = "signin", errorCode = "unknown") {
  trackEvent("auth_failure", {
    provider,
    mode,
    error_code: errorCode,
  });
}

export function trackAuthRedirectStarted(provider) {
  trackEvent("auth_redirect_started", { provider });
}

export function trackSignOut() {
  trackEvent("sign_out");
}

function getDealPayload(item = {}) {
  return {
    deal_id: item?.id || undefined,
    deal_title: item?.title || undefined,
    venue_id: item?.venueId || undefined,
    venue_slug: item?.venueSlug || undefined,
    venue_name: item?.venueName || undefined,
    venue_type: item?.venueType || undefined,
    venue_area: item?.area || undefined,
  };
}

export function trackDealOpened(item, destination = "venue") {
  trackEvent("deal_opened", {
    ...getDealPayload(item),
    destination,
  });
}

export function trackDealFeedLoaded(dealCount, venueCount) {
  trackEvent("deal_feed_loaded", {
    deal_count: dealCount,
    venue_count: venueCount,
  });
}

export function trackDealSwiped(direction, item) {
  trackEvent("deal_swiped", {
    ...getDealPayload(item),
    direction,
  });
}

export function trackDealFilterChanged(typeFilter, areaFilter, visibleCount) {
  trackEvent("deal_filter_changed", {
    type_filter: typeFilter,
    area_filter: areaFilter,
    visible_count: visibleCount,
  });
}

export function trackDealFilterDrawerToggled(isOpen, context = "unknown") {
  trackEvent(isOpen ? "deal_filter_drawer_opened" : "deal_filter_drawer_closed", {
    context,
  });
}
